import { Select } from '@ally/forms'
import {
  Accordion,
  Box,
  Label,
  Space,
  TextBody,
  upToMd,
} from '@ally/metronome-ui'
import React, { useEffect } from 'react'

export const BeneficiaryTypeField: React.FC<{
  isIraPage: boolean
  hasExistingBenes: boolean
  onBeneficiaryTypeSelectChange: (e: React.BaseSyntheticEvent) => void
  handleInputChange: (name: string, value: string) => void
  isMarried?: boolean
  hasPrimaryBene?: number
}> = ({
  isIraPage,
  hasExistingBenes,
  onBeneficiaryTypeSelectChange,
  handleInputChange,
  isMarried,
  hasPrimaryBene,
}) => {
  const showBeneficiaryTypeSelect = !isMarried && hasExistingBenes
  const showPrimary =
    !hasExistingBenes ||
    (isMarried && (hasPrimaryBene === undefined || hasPrimaryBene === 0))
  const showContingent =
    isMarried && hasPrimaryBene !== undefined && hasPrimaryBene !== 0
  const validateIraDropdowns = (val: string): string => {
    if (!val) {
      return 'Make a selection.'
    }
    // istanbul ignore next
    return ''
  }

  const beneficiaryTypeOptions = [
    {
      label: 'Primary',
      value: 'primary',
    },
    {
      label: 'Contingent',
      value: 'contingent',
    },
  ]

  useEffect(() => {
    // set form value of beneficiaryType to one of these values being hardcoded by logic
    // so it can be used later in the form
    // istanbul ignore next - low value, just sets the value in the form
    if (!showBeneficiaryTypeSelect) {
      if (showPrimary) {
        handleInputChange('beneficiaryType', 'primary')
      } else if (showContingent) {
        handleInputChange('beneficiaryType', 'contingent')
      } else {
        handleInputChange('beneficiaryType', '')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPrimary, showContingent, showBeneficiaryTypeSelect])

  if (!isIraPage) return <></>

  return (
    <>
      {showBeneficiaryTypeSelect && (
        <Box mb="md">
          <Box width={upToMd('88%', '50%')}>
            <Select
              name="beneficiaryType"
              options={beneficiaryTypeOptions}
              placeholder="Make a selection"
              label="Beneficiary Type"
              validate={validateIraDropdowns}
              onChange={onBeneficiaryTypeSelectChange}
              data-testid="beneficiary-type-dropdown"
            />
          </Box>
          <Accordion
            disableContainer
            items={[
              {
                heading: 'What are the types?',
                panel: (
                  <Space mb="sm">
                    <TextBody size="md" tag="div" tabIndex={-1}>
                      Primary beneficiary: Primary beneficiaries will receive
                      the funds in your IRA plan in the event of your death.
                    </TextBody>
                    <TextBody size="md" tag="div">
                      If you are not married, you can add multiple primary
                      beneficiaries and leave a percentage of the money in your
                      account to each one.
                    </TextBody>
                    <TextBody size="md" tag="div">
                      If you are married, your spouse must be your sole primary
                      beneficiary, unless your spouse consents to this change.
                      If you would like someone other than your spouse to be
                      your primary beneficiary, please complete and mail us an
                      IRA Change of Beneficiary form. Please remember that your
                      spouse&apos;s signature on this form has to be notarized.
                    </TextBody>
                    <TextBody size="md" tag="div">
                      Contingent beneficiary: Contingent beneficiaries will
                      receive the funds in your IRA plan if you and your primary
                      beneficiaries have died. You can leave a percentage of the
                      money in your plan to each one.
                    </TextBody>
                    <TextBody size="md" tag="div">
                      You can add up to 6 beneficiaries on your IRA.
                    </TextBody>
                  </Space>
                ),
              },
            ]}
          />
        </Box>
      )}
      {showPrimary && (
        <Box width={upToMd('88%', '50%')} mb="lg">
          <Label htmlFor="beneficiaryType">Beneficiary Type</Label>
          <TextBody tag="span" size="sm">
            Primary
          </TextBody>
        </Box>
      )}
      {!!showContingent && (
        <Box width={upToMd('88%', '50%')} mb="lg">
          <Label htmlFor="beneficiaryType">Beneficiary Type</Label>
          <TextBody tag="span" size="sm">
            Contingent
          </TextBody>
        </Box>
      )}
    </>
  )
}
